import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ArrowLeft, Save, FileSearch, BookOpen } from "lucide-react";
import { toast } from "sonner";

const FRAMEWORKS = [
  { key: "vsme", label: "Add-on VSME", desc: "Voluntary SME standard EFRAG — 40 disclosures volontarie per PMI" },
  { key: "esrs", label: "Add-on ESRS", desc: "European Sustainability Reporting Standards — 31 disclosures per organizzazioni soggette CSRD" },
];

export default function Disclosure() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [active, setActive] = useState(params.get("fw") || "vsme");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assessments/${id}/disclosure/${active}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [id, active]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setParams({ fw: active }, { replace: true }); }, [active, setParams]);

  function setVal(code, field, val) {
    setRows((rs) => rs.map((r) => r.code === code ? { ...r, [field]: val } : r));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/assessments/${id}/disclosure`, {
        framework: active,
        answers: rows.map((r) => ({
          code: r.code,
          answer_text: r.answer_text || "",
          numeric_value: r.numeric_value,
          note: r.note || "",
        })),
      });
      toast.success("Disclosure salvata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  const stats = useMemo(() => {
    const total = rows.length;
    const filled = rows.filter((r) => (r.answer_text && r.answer_text !== "") || r.numeric_value !== null).length;
    return { total, filled };
  }, [rows]);

  // Group rows by topic
  const grouped = useMemo(() => {
    const out = {};
    for (const r of rows) {
      const t = r.topic_label || "Altro";
      (out[t] = out[t] || []).push(r);
    }
    return out;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="disclosure-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-viva-600" />
            Disclosure aggiuntiva
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Compilazione facoltativa di disclosure secondo i framework <strong>VSME</strong> ed <strong>ESRS</strong>.
            Queste informazioni <span className="font-semibold text-slate-700">non concorrono</span> al rating V1-V7 ma
            vengono mostrate nel report finale a supporto dell'audit.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <div className="text-sm">
            <span className="text-slate-500">Compilati: </span>
            <span className="font-heading font-bold text-slate-900">{stats.filled}/{stats.total}</span>
          </div>
          <Button onClick={save} disabled={saving} className="bg-viva-600 hover:bg-viva-700" data-testid="save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Salva
          </Button>
        </div>
      </header>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="bg-slate-100">
          {FRAMEWORKS.map((f) => (
            <TabsTrigger key={f.key} value={f.key} data-testid={`tab-${f.key}`}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {FRAMEWORKS.map((f) => (
          <TabsContent key={f.key} value={f.key} className="space-y-4 pt-4">
            <Card className="p-4 rounded-2xl border-slate-100 shadow-soft bg-white">
              <p className="text-xs text-slate-600 flex items-start gap-2">
                <FileSearch className="w-4 h-4 text-viva-600 mt-0.5 shrink-0" />
                <span>{f.desc}</span>
              </p>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
              </div>
            ) : (
              Object.entries(grouped).map(([topic, items]) => (
                <Card key={topic} className="rounded-2xl border-slate-100 shadow-soft overflow-hidden">
                  <div className="px-5 py-3 bg-viva-50/40 border-b border-viva-100">
                    <div className="font-heading font-semibold text-viva-800">{topic}</div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {items.map((r) => (
                      <DisclosureRow key={r.code} row={r} onChange={setVal} />
                    ))}
                  </div>
                </Card>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="bg-viva-600 hover:bg-viva-700" data-testid="save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Salva disclosure
        </Button>
      </div>
    </div>
  );
}

function DisclosureRow({ row, onChange }) {
  return (
    <div className="px-5 py-4 grid grid-cols-12 gap-3 hover:bg-slate-50/40" data-testid={`disc-row-${row.code}`}>
      <div className="col-span-12 md:col-span-7">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-bold text-viva-700 shrink-0">{row.code}</span>
          <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 rounded">
            {row.type === "B" ? "SI/NO" : row.type === "Q" ? "Quantitativo" : "Likert"}
          </span>
          {row.unit && <span className="text-[10px] text-slate-400">{row.unit}</span>}
        </div>
        <div className="text-sm text-slate-800 mt-1 whitespace-pre-line">{row.question}</div>
        {row.evidenza && (
          <details className="mt-1.5 group">
            <summary className="cursor-pointer text-[11px] font-semibold text-viva-700 hover:text-viva-800 list-none inline-flex items-center gap-1">
              <span className="opacity-70 group-open:rotate-90 inline-block transition-transform">▸</span>
              Evidenza richiesta
            </summary>
            <div className="mt-1 text-[12px] text-slate-600 whitespace-pre-line bg-viva-50/60 border-l-2 border-viva-400 pl-3 py-1.5 rounded-r">
              {row.evidenza}
            </div>
          </details>
        )}
      </div>
      <div className="col-span-6 md:col-span-2">
        {row.type === "Q" ? (
          <Input
            type="number"
            placeholder="Valore numerico"
            value={row.numeric_value ?? ""}
            onChange={(e) => onChange(row.code, "numeric_value", e.target.value === "" ? null : Number(e.target.value))}
            className="h-9 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500"
            data-testid={`disc-num-${row.code}`}
          />
        ) : row.type === "L" ? (
          <select
            value={row.answer_text || ""}
            onChange={(e) => onChange(row.code, "answer_text", e.target.value)}
            className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm focus:ring-2 focus:ring-viva-500/20 focus:border-viva-500"
            data-testid={`disc-likert-${row.code}`}
          >
            <option value="">—</option>
            <option value="L1">L1 · Iniziale</option>
            <option value="L2">L2 · In sviluppo</option>
            <option value="L3">L3 · Strutturato</option>
            <option value="L4">L4 · Integrato</option>
            <option value="L5">L5 · Leader</option>
          </select>
        ) : (
          <div className="flex gap-1">
            {["SI", "NO", "NA"].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(row.code, "answer_text", row.answer_text === opt ? "" : opt)}
                className={`flex-1 h-9 rounded-md text-xs font-bold border transition-colors ${row.answer_text === opt ? "bg-viva-500 text-white border-viva-500" : "bg-white text-slate-500 border-slate-200 hover:border-viva-300"}`}
                data-testid={`disc-${row.code}-${opt}`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-6 md:col-span-3">
        <Input
          placeholder="Nota / riferimento documento"
          value={row.note || ""}
          onChange={(e) => onChange(row.code, "note", e.target.value)}
          className="h-9 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500"
          data-testid={`disc-note-${row.code}`}
        />
      </div>
    </div>
  );
}
